<template>
  <main class="sf-page">
    <div v-if="loading" class="sf-loader"><span class="sf-loader__ring"></span></div>
    <div v-else-if="error" class="sf-section sf-section--cream">
      <div class="sf-container sf-error">
        <div class="sf-error__icon-wrap"><X /></div>
        <h2 class="sf-h2">Projet non trouvé</h2>
        <router-link to="/projects"><button class="sf-btn sf-btn--terra"><ArrowLeft class="sf-btn__icon" />Retour</button></router-link>
      </div>
    </div>

    <template v-else-if="project">
      <!-- Hero -->
      <div class="sp-hero">
        <img v-if="project.featured_image" :src="imageUrl+project.featured_image" :alt="project.title" loading="lazy" class="sp-hero__img" />
        <div v-else class="sp-hero__fallback"><component :is="getIconComponent(project.icon)" /></div>
        <div class="sp-hero__overlay"></div>
        <div class="sp-hero__content">
          <div class="sp-hero__badges">
            <span v-if="project.is_featured" class="sf-card__badge" style="position:static">Projet Phare</span>
            <span class="sf-card__cat" style="position:static">{{ project.category }}</span>
            <span :class="getStatusClass(project.status)">{{ project.status_label }}</span>
          </div>
          <h1 class="sp-hero__title">{{ project.title }}</h1>
          <p class="sp-hero__sub">{{ project.description }}</p>
        </div>
      </div>

      <section class="sf-section sf-section--cream">
        <div class="sf-container">
          <router-link to="/projects" class="sf-back-link"><ArrowLeft />Retour aux projets</router-link>

          <!-- KPI row -->
          <div class="sp-kpis">
            <div class="sp-kpi"><Calendar /><div><small>Début</small><strong>{{ formatDate(project.start_date) }}</strong></div></div>
            <div class="sp-kpi"><MapPin /><div><small>Localisation</small><strong>{{ project.location }}</strong></div></div>
            <div class="sp-kpi"><Users /><div><small>Bénéficiaires</small><strong>{{ project.formatted_beneficiaries||project.beneficiaries+'+' }}</strong></div></div>
            <div class="sp-kpi sp-kpi--progress"><div style="flex:1"><small style="display:block;margin-bottom:.4rem;color:var(--ink-60);font-size:.75rem">Avancement</small><div class="sf-progress"><div class="sf-progress__track"><div class="sf-progress__fill" :style="{'--target':project.progress+'%'}"></div></div><span class="sf-progress__pct">{{ project.progress }}%</span></div></div></div>
          </div>

          <!-- Layout -->
          <div class="sp-layout">
            <!-- Main -->
            <div class="sp-main">
              <div class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>À propos du projet</h3></div>
                <div class="sf-sidebar-card__body"><p style="font-size:.9rem;color:var(--ink-60);line-height:1.7;word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;">{{ project.full_description||project.description }}</p></div>
              </div>
              <div v-if="project.objectives?.length" class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>Objectifs</h3></div>
                <div class="sf-sidebar-card__body">
                  <ol class="sp-obj-list">
                    <li v-for="(obj,i) in project.objectives" :key="i"><span class="sp-obj-num">{{ String(i+1).padStart(2,'0') }}</span><span>{{ obj }}</span></li>
                  </ol>
                </div>
              </div>
              <div v-if="project.achievements?.length" class="sf-sidebar-card" style="margin-bottom:1.5rem">
                <div class="sf-sidebar-card__header"><h3>Réalisations à ce jour</h3></div>
                <div class="sf-sidebar-card__body">
                  <ul class="sp-ach-list">
                    <li v-for="(ach,i) in project.achievements" :key="i"><span class="sp-ach-dot"></span>{{ ach }}</li>
                  </ul>
                </div>
              </div>
              <!-- Gallery -->
              <div v-if="project.gallery?.length" class="sf-sidebar-card">
                <div class="sf-sidebar-card__header"><h3>Galerie du projet</h3></div>
                <div class="sf-sidebar-card__body">
                  <div class="sf-gallery">
                    <div v-for="(img,i) in project.gallery" :key="i" class="sf-gallery-item" style="height:160px" @click="openGallery(i)">
                      <img :src="imageUrl+(img.url||img)" loading="lazy" :alt="`Galerie ${i+1}`" />
                      <div class="sf-gallery-item__overlay"><ZoomIn /></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sidebar -->
            <div class="sp-sidebar">
              <div v-if="project.budget" class="sf-sidebar-card" style="margin-bottom:1.25rem">
                <div class="sf-sidebar-card__header"><h3>Budget</h3></div>
                <div class="sf-sidebar-card__body">
                  <p style="font-family:var(--fd);font-size:2rem;font-weight:700;color:var(--terra)">{{ project.formatted_budget||project.budget+' €' }}</p>
                  <p style="font-size:.8rem;color:var(--ink-60);margin-top:.25rem">Budget total</p>
                </div>
              </div>
              <div class="sf-sidebar-card" style="margin-bottom:1.25rem">
                <div class="sf-sidebar-card__header"><h3>Soutenez ce projet</h3></div>
                <div class="sf-sidebar-card__body">
                  <p style="font-size:.84rem;color:var(--ink-60);line-height:1.6;margin-bottom:1rem">Votre don nous aide à étendre l'accès Internet et former plus de jeunes.</p>
                  <router-link to="/faire-un-don"><button class="sf-btn sf-btn--terra sf-btn--full">Faire un don <ArrowRight class="sf-btn__icon" /></button></router-link>
                </div>
              </div>
              <div class="sf-sidebar-card" style="margin-bottom:1.25rem">
                <div class="sf-sidebar-card__header"><h3>Devenir partenaire</h3></div>
                <div class="sf-sidebar-card__body">
                  <p style="font-size:.84rem;color:var(--ink-60);line-height:1.6;margin-bottom:1rem">Rejoignez notre réseau d'associations partenaires pour multiplier l'impact.</p>
                  <router-link to="/become-partner"><button class="sf-btn sf-btn--outline sf-btn--full sf-btn--sm">En savoir plus</button></router-link>
                </div>
              </div>
              <div class="sf-sidebar-card">
                <div class="sf-sidebar-card__header"><h3>Partager</h3></div>
                <div class="sf-sidebar-card__body" style="display:flex;gap:.6rem">
                  <button @click="shareProject" class="sf-btn sf-btn--outline sf-btn--sm" style="flex:1;justify-content:center"><Share2 class="sf-btn__icon" />Partager</button>
                  <button @click="showContactForm" class="sf-btn sf-btn--outline sf-btn--sm" style="flex:1;justify-content:center"><Mail class="sf-btn__icon" />Contact</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Related -->
          <div v-if="relatedProjects.length" style="margin-top:3rem;padding-top:2rem;border-top:1px solid var(--ink-20)">
            <span class="sf-label">Projets similaires</span>
            <div class="sp-related-grid">
              <div v-for="rp in relatedProjects" :key="rp.id" class="sp-related-card">
                <h3>{{ rp.title }}</h3>
                <p>{{ rp.description }}</p>
                <router-link :to="`/projects/${rp.slug||rp.id}`"><button class="sf-btn sf-btn--outline sf-btn--full sf-btn--sm" style="margin-top:.85rem">Découvrir <ArrowRight class="sf-btn__icon" /></button></router-link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Gallery modal -->
      <div v-if="showGalleryModal" class="sf-modal" @click.self="closeGallery">
        <button class="sf-modal__close" @click="closeGallery"><X /></button>
        <div style="position:relative;max-width:900px;width:100%">
          <button v-if="currentGalleryIndex>0" class="sf-modal__nav sf-modal__nav--prev" @click="prevImage"><ChevronLeft /></button>
          <img v-if="project.gallery[currentGalleryIndex]" :src="imageUrl+(project.gallery[currentGalleryIndex].url||project.gallery[currentGalleryIndex])" style="max-height:80vh;object-fit:contain;margin:0 auto;display:block" />
          <button v-if="currentGalleryIndex<project.gallery.length-1" class="sf-modal__nav sf-modal__nav--next" @click="nextImage"><ChevronRight /></button>
          <p class="sf-modal__counter">{{ currentGalleryIndex+1 }} / {{ project.gallery.length }}</p>
        </div>
      </div>
    </template>
    <Transition name="toast"><div v-if="toastMessage" class="sf-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AOS from 'aos'; import 'aos/dist/aos.css'
import { ArrowLeft, ArrowRight, Calendar, MapPin, Users, Target, Wifi, BookOpen, Leaf, Share2, Mail, ZoomIn, X, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-vue-next'
export default {
  name:'ProjectDetailPage',
  components:{ ArrowLeft, ArrowRight, Calendar, MapPin, Users, Target, Wifi, BookOpen, Leaf, Share2, Mail, ZoomIn, X, ChevronLeft, ChevronRight, CheckCircle },
  data(){return{showGalleryModal:false,currentGalleryIndex:0,toastMessage:''}},
  computed:{...mapState({project:s=>s.currentProject,relatedProjects:s=>s.relatedProjects,loading:s=>s.loading,error:s=>s.error,imageUrl:s=>s.imageUrl})},
  watch:{'$route.params.slug':{handler(s){if(s)this.loadProject(s)},immediate:true},toastMessage(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}},
  mounted(){AOS.init({once:true,offset:70,easing:'ease-out-cubic',duration:650})},
  methods:{
    ...mapActions(['fetchProjectBySlug']),
    async loadProject(s){try{await this.fetchProjectBySlug(s)}catch(e){console.error(e)}},
    shareProject(){if(navigator.share)navigator.share({title:this.project.title,url:window.location.href}).then(()=>{this.toastMessage='Partagé !'}).catch(()=>{});else navigator.clipboard.writeText(window.location.href).then(()=>{this.toastMessage='Lien copié !'}).catch(()=>{})},
    showContactForm(){this.toastMessage='Formulaire de contact — Bientôt disponible'},
    openGallery(i){this.currentGalleryIndex=i;this.showGalleryModal=true;document.body.style.overflow='hidden'},
    closeGallery(){this.showGalleryModal=false;document.body.style.overflow='auto'},
    prevImage(){if(this.currentGalleryIndex>0)this.currentGalleryIndex--},
    nextImage(){if(this.currentGalleryIndex<this.project.gallery.length-1)this.currentGalleryIndex++},
    getIconComponent(n){return{Wifi,Users,Leaf,BookOpen}[n]||Wifi},
    getStatusClass(s){const m={actif:'sp-status sp-status--actif',planifié:'sp-status',terminé:'sp-status sp-status--done',en_pause:'sp-status sp-status--pause'};return m[s]||'sp-status'},
    formatDate(d){return d?new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'long',year:'numeric'}):'N/A'}
  },
  beforeUnmount(){document.body.style.overflow='auto'}
}
</script>
<style>@import './sf-design.css';</style>
<style scoped>
.sp-hero{position:relative;height:400px;overflow:hidden;background:var(--forest)}
.sp-hero__img{width:100%;height:100%;object-fit:cover}
.sp-hero__fallback{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--sage)}
.sp-hero__fallback :deep(svg){width:64px;height:64px}
.sp-hero__overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(20,18,12,.85),rgba(20,18,12,.25) 60%,transparent)}
.sp-hero__content{position:absolute;bottom:2.5rem;left:0;right:0;padding:0 2rem;max-width:1200px;margin:0 auto}
.sp-hero__badges{display:flex;gap:.5rem;flex-wrap:wrap;margin-bottom:.75rem}
.sp-status{display:inline-flex;font-size:.7rem;padding:.3rem .75rem;border-radius:2px;border:1px solid rgba(255,255,255,.3);color:rgba(255,255,255,.7)}
.sp-status--actif{background:rgba(122,158,135,.3);border-color:rgba(122,158,135,.5);color:var(--cream)}
.sp-status--done{background:rgba(200,90,42,.2);border-color:rgba(200,90,42,.4);color:var(--cream)}
.sp-status--pause{background:rgba(232,147,58,.2);border-color:rgba(232,147,58,.4);color:var(--cream)}
.sp-hero__title{font-family:var(--fd);font-size:clamp(2rem,6vw,3.8rem);font-weight:600;color:var(--white);line-height:1.1;margin-bottom:.6rem}
.sp-hero__sub{font-size:1rem;color:rgba(245,239,224,.72);word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;line-height:1.6;max-width:60ch}
.sp-kpis{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:1rem;margin:2rem 0 2.5rem}
.sp-kpi{background:var(--white);border-radius:8px;padding:1rem;display:flex;align-items:flex-start;gap:.75rem;box-shadow:var(--shadow-sm)}
.sp-kpi :deep(svg){width:18px;height:18px;color:var(--terra);flex-shrink:0;margin-top:2px}
.sp-kpi small{display:block;font-size:.72rem;color:var(--ink-60);margin-bottom:.2rem}
.sp-kpi strong{font-size:.92rem;color:var(--ink);font-weight:500}
.sp-kpi--progress{grid-column:span 2}
@media(max-width:540px){.sp-kpi--progress{grid-column:span 1}}
.sp-layout{display:grid;grid-template-columns:1fr 300px;gap:2rem;align-items:start}
@media(max-width:900px){.sp-layout{grid-template-columns:1fr}}
.sp-obj-list{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.85rem}
.sp-obj-list li{display:flex;align-items:flex-start;gap:.75rem;font-size:.88rem;color:var(--ink-60);line-height:1.55}
.sp-obj-num{font-family:var(--fd);font-size:1.1rem;font-weight:700;color:var(--terra);flex-shrink:0;width:28px}
.sp-ach-list{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.7rem}
.sp-ach-list li{display:flex;align-items:flex-start;gap:.7rem;font-size:.88rem;color:var(--ink-60);line-height:1.55}
.sp-ach-dot{width:6px;height:6px;border-radius:50%;background:var(--terra);flex-shrink:0;margin-top:6px}
.sp-related-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:1.25rem;margin-top:.75rem}
.sp-related-card{background:var(--white);border-radius:8px;padding:1.5rem;box-shadow:var(--shadow-sm);transition:transform .3s ease,box-shadow .3s ease}
.sp-related-card:hover{transform:translateY(-3px);box-shadow:var(--shadow-md)}
.sp-related-card h3{font-family:var(--fd);font-size:1.1rem;font-weight:600;color:var(--ink);margin-bottom:.4rem}
.sp-related-card p{font-size:.83rem;color:var(--ink-60);line-height:1.55;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
</style>
